import AbstractModel from './utils/abstractModel'
import types from './utils/types'
import { INCENTIVE_TYPES } from './corporate'

function normalizeTrackingParamsFromFields(json) {
  if (Object(json) !== json) return {}
  const fields = {
    utm_source: json.utm_source,
    utm_medium: json.utm_medium,
    utm_campaign: json.utm_campaign,
    utm_term: json.utm_term,
    utm_content: json.utm_content,
  }

  return Object.fromEntries(
    Object.entries(fields)
      .filter(([, value]) => value != null && value !== '')
      .map(([key, value]) => [key, value?.toString?.() ?? '']),
  )
}

function normalizeIncentive(incentive) {
  if (Object(incentive) !== incentive) return null

  const incentiveType =
    INCENTIVE_TYPES[incentive.incentive_type] || incentive.incentive_type

  if (
    !incentiveType ||
    !incentive.banner_title ||
    !incentive.banner_description
  ) {
    return null
  }

  return {
    incentiveType,
    bannerTitle: incentive.banner_title,
    bannerDescription: incentive.banner_description,
  }
}

class LandingPage extends AbstractModel {
  /**
   * @type {string}
   * @readonly
   */
  slug

  /**
   * @type {Object.<string, string>}
   * @readonly
   */
  trackingParams

  /**
   * @type {string|null}
   * @readonly
   */
  displayTitle

  /**
   * @type {string|null}
   * @readonly
   */
  logo

  /**
   * @type {string|null}
   * @readonly
   */
  metaPixelKey

  /**
   * @type {boolean}
   * @readonly
   */
  skipLandingPage

  /**
   * @type {string|null}
   * @readonly
   */
  variant

  /**
   * @type {string|null}
   * @readonly
   */
  eligibilityVerificationType

  /**
   * @type {string|null}
   * @readonly
   */
  eligibilityVerificationExternalUrl

  /**
   * @type {string|null}
   * @readonly
   */
  eligibilityVerificationExternalPortalName

  /**
   * @type {string|null}
   * @readonly
   */
  memberIdTitle

  /**
   * @type {{incentiveType: string, bannerTitle: string, bannerDescription: string}|null}
   * @readonly
   */
  incentive

  constructor(json) {
    super(json)

    this.slug = json.slug ?? ''
    this.trackingParams = normalizeTrackingParamsFromFields(json)
    this.displayTitle = json.display_title ?? null
    this.logo = json.logo ?? null
    this.metaPixelKey = json.meta_pixel_key ?? null
    this.skipLandingPage = json.skip_landing_page === true
    this.variant = json.variant ?? null
    this.eligibilityVerificationType =
      json.eligibility_verification_type ?? null
    this.eligibilityVerificationExternalUrl =
      json.eligibility_verification_external_url ?? null
    this.eligibilityVerificationExternalPortalName =
      json.eligibility_verification_external_portal_name ?? null
    this.memberIdTitle = json.member_id_title ?? null
    this.incentive = normalizeIncentive(json.incentive)

    this.validate()
  }

  validate() {
    this.assertType('slug', [types.STRING])
    this.assertType('trackingParams', [types.JSON])
    this.assertType('displayTitle', [types.STRING, types.NULL])
    this.assertType('logo', [types.STRING, types.NULL])
    this.assertType('metaPixelKey', [types.STRING, types.NULL])
    this.assertType('skipLandingPage', [types.BOOLEAN])
    this.assertType('variant', [types.STRING, types.NULL])
    this.assertType('eligibilityVerificationType', [types.STRING, types.NULL])
    this.assertType('eligibilityVerificationExternalUrl', [
      types.STRING,
      types.NULL,
    ])
    this.assertType('eligibilityVerificationExternalPortalName', [
      types.STRING,
      types.NULL,
    ])
    this.assertType('memberIdTitle', [types.STRING, types.NULL])
    this.assertType('incentive', [types.JSON, types.NULL])
  }
}

export default LandingPage
